import { useEffect } from 'react';

import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { TooltipProvider } from '@radix-ui/react-tooltip';
import { hashSAMLSub } from '@remarkable/sentry-utils/common';
import * as Sentry from '@sentry/react';
import { RouterProvider } from '@tanstack/react-router';
// eslint-disable-next-line import/no-unresolved
import { Analytics } from '@vercel/analytics/react';

import { UserAuthenticated } from './ampli';
import { tracker } from './analytics/tracker';
import { ApiProvider } from './api/useApi';
import { DragStateProvider } from './apps/myFiles/app/providers/DragStateProvider';
import { auth0Client, resolveAuth0Client } from './auth0Client';
import { RouteLoadingSpinner } from './components';
import { CONFIG, IS_DEVELOPMENT } from './config';
import { I18nProvider } from './providers/I18nProvider';
import { LaunchDarklyProvider } from './providers/LaunchDarklyProvider';
import { router } from './router';
import {
  useAccountLinkingToken,
  useSetAccountLinkingToken,
} from './utils/accountLinkingTokenStore';
import { authHelpers } from './utils/authHelpers';
import { parseAuthReturnSearch } from './utils/parseSearch';
import { useFeatureFlags } from './utils/useFeatureFlags';
import { userHelpers } from './utils/userHelpers';
import { useSyncAccessTokenSubscriptionStatus } from './utils/useSyncAccessTokenSubscriptionStatus';
import { useTheme } from './utils/useTheme';
import { useTrackFeatureFlags } from './utils/useTrackFeatureFlags';

let authLoadingSpan: Sentry.Span | null = null;

export function App() {
  const setAccountLinkingToken = useSetAccountLinkingToken();

  useEffect(() => {
    Sentry.startSpanManual(
      {
        name: 'Auth loading',
        op: 'app.lifecycle',
      },
      (span) => {
        if (authLoadingSpan === null) {
          authLoadingSpan = span;
        }
      }
    );
  }, []);

  const { theme } = useTheme();
  if (!CONFIG.Auth0ClientID || !CONFIG.Auth0Domain) {
    throw new Error('Missing auth0 config');
  }

  return (
    <div data-theme={theme} className={theme.includes('dark') ? 'dark' : ''}>
      <I18nProvider>
        <TooltipProvider>
          <Auth0Provider
            clientId={CONFIG.Auth0ClientID}
            domain={CONFIG.Auth0Domain}
            skipRedirectCallback={
              // Integration authentication flows return with code and state params
              // that should be ignored by Auth0.

              // Google Drive and Dropbox will return to the same url, but with a
              // callback search param.
              (window.location.pathname.includes('/integrations') &&
                window.location.search.includes('callback')) ||
              // Microsoft OneDrive returns on a `/callback` subroute, which on our
              // end is redirected back to the parent route with the same callback
              // search param as the other two integration flows. Who knows why it
              // is different.
              window.location.pathname.includes('/integrations/callback')
            }
            authorizationParams={{
              redirect_uri: window.location.origin,
              audience: CONFIG.Auth0Audience || CONFIG.CloudApiUrl,
            }}
            onRedirectCallback={async (appState) => {
              if (typeof appState?.accountLinkingAccessToken === 'string') {
                setAccountLinkingToken(appState.accountLinkingAccessToken);
              }

              if (appState?.returnTo) {
                const url = new URL(appState.returnTo, window.location.origin);
                await router.navigate({
                  to: url.pathname,
                  search: parseAuthReturnSearch(url),
                  hash: url.hash,
                  replace: true,
                });
              } else {
                await router.navigate({
                  to: '/',
                  replace: true,
                });
              }
            }}
          >
            <LaunchDarklyProvider>
              <ApiProvider withDevtools={true}>
                <DragStateProvider>
                  <InnerApp />
                </DragStateProvider>
              </ApiProvider>
            </LaunchDarklyProvider>
          </Auth0Provider>
        </TooltipProvider>
      </I18nProvider>
      <Analytics />
    </div>
  );
}

function InnerApp() {
  const auth = useAuth0();
  const featureFlags = useFeatureFlags();
  useTrackFeatureFlags();
  const accountLinkingToken = useAccountLinkingToken();
  useSyncAccessTokenSubscriptionStatus();

  useEffect(() => {
    if (auth.isLoading) return;

    if (authLoadingSpan) {
      authLoadingSpan.end();
      authLoadingSpan = null;
    }
  }, [auth.isLoading]);

  useEffect(() => {
    if (auth.isLoading) return;

    async function updateSentryUser() {
      if (auth.user) {
        Sentry.setUser({
          id: await hashSAMLSub(auth.user.sub),
          email: IS_DEVELOPMENT ? auth.user.email : undefined,
        });
      }
    }

    async function trackAuthenticated() {
      const token = await authHelpers.getAccessTokenSilently(auth);

      void tracker.trackEvent(
        new UserAuthenticated({
          auth0_jwt: token,
        })
      );
      if (auth.user) {
        void tracker.identify({
          myrm_user_parameters: JSON.stringify({
            is_organization_member: userHelpers.isOrganizationMember(auth.user),
          }),
        });
        // Add experiment here if you want to track subscription status as part of experiment
        // void getAppliedExperimentVariant(
        //   'test-experiment',
        //   auth.user.sub
        // );
      }
    }

    if (auth.isAuthenticated) {
      void trackAuthenticated();
      void updateSentryUser();
    }

    resolveAuth0Client(auth);
  }, [auth, auth.isLoading]);

  // Wait for auth to be fully loaded and redirect callback to be handled before
  // rendering the app.
  if (auth.isLoading) {
    return <RouteLoadingSpinner />;
  }

  return (
    <RouterProvider
      router={router}
      context={{ auth: auth0Client, featureFlags, accountLinkingToken }}
    />
  );
}
